import React, { useState, useEffect, useCallback, useRef } from 'react';
import SmartImage from '../common/SmartImage';

export default function Slider({ slides, autoPlay = true, autoPlayDelay = 5000 }) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const timerRef = useRef(null);

  const len = slides.length;

  const goTo = useCallback((i) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(i);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => goTo((current + 1) % len), [current, len, goTo]);
  const prev = useCallback(() => goTo((current - 1 + len) % len), [current, len, goTo]);

  // Auto-play
  useEffect(() => {
    if (!autoPlay || len <= 1) return;
    timerRef.current = setInterval(next, autoPlayDelay);
    return () => clearInterval(timerRef.current);
  }, [autoPlay, autoPlayDelay, next, len]);

  // Pause on hover
  const pauseAuto = () => { if (timerRef.current) clearInterval(timerRef.current); };
  const resumeAuto = () => {
    if (autoPlay && len > 1) {
      timerRef.current = setInterval(next, autoPlayDelay);
    }
  };

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  // Resize
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Touch swipe
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? next() : prev();
    }
    setTouchStart(null);
  };

  if (!slides || len === 0) return null;

  return (
    <div
      className="sld"
      onMouseEnter={pauseAuto}
      onMouseLeave={resumeAuto}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Track */}
      <div
        className="sld-track"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {slides.map((slide, i) => {
          const imgSrc = (viewportWidth <= 768 && slide.imageMobile) ? slide.imageMobile : slide.image;
          const isActive = i === current;
          return (
          <div
            key={slide.id || slide._id || i}
            className={`sld-slide ${isActive ? 'is-active' : ''}`}
            style={{ background: slide.background || '#0f172a' }}
          >
            <SmartImage
              src={imgSrc}
              alt={slide.title || `Slide ${i + 1}`}
              loading="lazy"
            />
            {/* Overlay gradient */}
            <div className="sld-overlay"></div>

            {/* Content overlay (if title exists) */}
            {slide.title && (
              <div className="sld-content" key={isActive ? `${i}-active-${current}` : `${i}-idle`}>
                {slide.subtitle && <span className="sld-badge">{slide.subtitle}</span>}
                <h2 className="sld-title">{slide.title}</h2>
                {slide.description && <p className="sld-desc">{slide.description}</p>}
                {slide.buttonText && (
                  <button className="sld-cta">{slide.buttonText}</button>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      {len > 1 && (
        <>
          <button className="sld-arrow sld-arrow-left" onClick={prev} aria-label="Précédent">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button className="sld-arrow sld-arrow-right" onClick={next} aria-label="Suivant">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        </>
      )}

      {/* Dots */}
      {len > 1 && (
        <div className="sld-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`sld-dot ${i === current ? 'active' : ''}`}
              aria-label={`Diapo ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Progress bar */}
      {len > 1 && autoPlay && (
        <div className="sld-progress">
          <div
            className="sld-progress-bar"
            key={current}
            style={{ animationDuration: `${autoPlayDelay}ms` }}
          />
        </div>
      )}
    </div>
  );
}
